import { z } from 'zod';

// Backend types supported by Triton
export const BACKEND_TYPES = [
  { id: 'onnxruntime', name: 'ONNX Runtime', platform: 'onnxruntime_onnx' },
  { id: 'tensorrt', name: 'TensorRT', platform: 'tensorrt_plan' },
  { id: 'pytorch', name: 'PyTorch (TorchScript)', platform: 'pytorch_libtorch' },
  { id: 'python', name: 'Python Backend', platform: 'python' },
  { id: 'openvino', name: 'OpenVINO', platform: 'openvino' },
] as const;

export type BackendType = (typeof BACKEND_TYPES)[number]['id'];

// Config templates for each backend
export const CONFIG_TEMPLATES: Record<BackendType, string> = {
  onnxruntime: `name: "MODEL_NAME"
platform: "onnxruntime_onnx"
max_batch_size: MAX_BATCH_SIZE
input [
  {
    name: "input"
    data_type: TYPE_FP32
    dims: [ -1, 3, 224, 224 ]
  }
]
output [
  {
    name: "output"
    data_type: TYPE_FP32
    dims: [ -1, 1000 ]
  }
]
instance_group [
  {
    count: 1
    kind: KIND_GPU
  }
]`,
  tensorrt: `name: "MODEL_NAME"
platform: "tensorrt_plan"
max_batch_size: MAX_BATCH_SIZE
input [
  {
    name: "input"
    data_type: TYPE_FP32
    dims: [ 3, 224, 224 ]
  }
]
output [
  {
    name: "output"
    data_type: TYPE_FP32
    dims: [ 1000 ]
  }
]
instance_group [
  {
    count: 1
    kind: KIND_GPU
  }
]`,
  pytorch: `name: "MODEL_NAME"
platform: "pytorch_libtorch"
max_batch_size: MAX_BATCH_SIZE
input [
  {
    name: "INPUT__0"
    data_type: TYPE_FP32
    dims: [ 3, 224, 224 ]
  }
]
output [
  {
    name: "OUTPUT__0"
    data_type: TYPE_FP32
    dims: [ 1000 ]
  }
]
instance_group [
  {
    count: 1
    kind: KIND_GPU
  }
]`,
  python: `name: "MODEL_NAME"
backend: "python"
max_batch_size: MAX_BATCH_SIZE
input [
  {
    name: "text"
    data_type: TYPE_STRING
    dims: [ -1 ]
  }
]
output [
  {
    name: "embedding"
    data_type: TYPE_FP32
    dims: [ -1, 768 ]
  }
]
instance_group [
  {
    count: 1
    kind: KIND_GPU
  }
]`,
  openvino: `name: "MODEL_NAME"
platform: "openvino"
max_batch_size: MAX_BATCH_SIZE
input [
  {
    name: "input"
    data_type: TYPE_FP32
    dims: [ 3, 224, 224 ]
  }
]
output [
  {
    name: "output"
    data_type: TYPE_FP32
    dims: [ 1000 ]
  }
]
instance_group [
  {
    count: 1
    kind: KIND_CPU
  }
]`,
};

// Upload Model Schema
export const uploadModelSchema = z.object({
  modelName: z
    .string()
    .min(1, 'Model name is required')
    .max(64, 'Model name must be less than 64 characters')
    .regex(
      /^[a-z0-9][a-z0-9_-]*[a-z0-9]$|^[a-z0-9]$/,
      'Model name must start and end with alphanumeric characters and contain only lowercase letters, numbers, hyphens, and underscores'
    ),
  backend: z.enum(['onnxruntime', 'tensorrt', 'pytorch', 'python', 'openvino'], {
    required_error: 'Backend type is required',
  }),
  version: z
    .string()
    .min(1, 'Version is required')
    .regex(/^\d+$/, 'Version must be a numeric string (e.g., "1", "2")'),
  maxBatchSize: z.coerce
    .number()
    .min(1, 'Max batch size must be at least 1')
    .max(256, 'Max batch size must be less than 256'),
  configPbtxt: z.string().min(1, 'Config.pbtxt is required'),
});

export type UploadModelFormData = z.infer<typeof uploadModelSchema>;

// Delete Model Schema
export const deleteModelSchema = z.object({
  confirmName: z.string().min(1, 'Please type the model name to confirm'),
});

export type DeleteModelFormData = z.infer<typeof deleteModelSchema>;

// Test Model Schema
export const testModelSchema = z.object({
  modelName: z.string().min(1, 'Please select a model'),
  inputJson: z.string().min(1, 'Input JSON is required').refine(
    (val) => {
      try {
        JSON.parse(val);
        return true;
      } catch {
        return false;
      }
    },
    { message: 'Invalid JSON format' }
  ),
});

export type TestModelFormData = z.infer<typeof testModelSchema>;

// Mock input templates for different model types
export const getDefaultInputJson = (modelName: string, backend: string): string => {
  if (modelName.includes('embedding') || modelName.includes('bge') || modelName.includes('bert')) {
    return JSON.stringify(
      {
        inputs: [
          {
            name: 'text',
            shape: [1],
            datatype: 'BYTES',
            data: ['Hello, this is a test sentence for embeddings.'],
          },
        ],
      },
      null,
      2
    );
  }

  if (modelName.includes('yolo') || modelName.includes('detection')) {
    return JSON.stringify(
      {
        inputs: [
          {
            name: 'images',
            shape: [1, 3, 640, 640],
            datatype: 'FP32',
            data: '<base64_encoded_image_tensor>',
          },
        ],
      },
      null,
      2
    );
  }

  if (modelName.includes('whisper') || modelName.includes('speech')) {
    return JSON.stringify(
      {
        inputs: [
          {
            name: 'audio',
            shape: [1, 16000],
            datatype: 'FP32',
            data: '<base64_encoded_audio_samples>',
          },
        ],
      },
      null,
      2
    );
  }

  if (modelName.includes('classifier') || modelName.includes('resnet')) {
    return JSON.stringify(
      {
        inputs: [
          {
            name: 'input',
            shape: [1, 3, 224, 224],
            datatype: 'FP32',
            data: '<base64_encoded_image_tensor>',
          },
        ],
      },
      null,
      2
    );
  }

  // Default generic input
  return JSON.stringify(
    {
      inputs: [
        {
          name: 'input',
          shape: [1, 3, 224, 224],
          datatype: 'FP32',
          data: '<tensor_data>',
        },
      ],
    },
    null,
    2
  );
};

// Mock response generator
export const generateMockResponse = (
  modelName: string,
  backend: string
): { output: object; latencyMs: number } => {
  const baseLatency = Math.random() * 50 + 10;

  if (modelName.includes('embedding') || modelName.includes('bge') || modelName.includes('bert')) {
    return {
      output: {
        model_name: modelName,
        model_version: '1',
        outputs: [
          {
            name: 'embedding',
            shape: [1, 1024],
            datatype: 'FP32',
            data: Array(10)
              .fill(0)
              .map(() => (Math.random() * 2 - 1).toFixed(6))
              .concat(['...']),
          },
        ],
      },
      latencyMs: Math.round(baseLatency + 5),
    };
  }

  if (modelName.includes('yolo') || modelName.includes('detection')) {
    return {
      output: {
        model_name: modelName,
        model_version: '1',
        outputs: [
          {
            name: 'detections',
            datatype: 'FP32',
            data: {
              boxes: [
                [120, 45, 380, 290],
                [450, 100, 620, 350],
              ],
              scores: [0.95, 0.87],
              classes: ['person', 'car'],
            },
          },
        ],
      },
      latencyMs: Math.round(baseLatency + 20),
    };
  }

  if (modelName.includes('whisper') || modelName.includes('speech')) {
    return {
      output: {
        model_name: modelName,
        model_version: '1',
        outputs: [
          {
            name: 'transcription',
            datatype: 'BYTES',
            data: {
              text: 'Hello, this is a sample transcription from the audio input.',
              segments: [
                { start: 0.0, end: 2.5, text: 'Hello, this is' },
                { start: 2.5, end: 5.0, text: 'a sample transcription' },
                { start: 5.0, end: 7.2, text: 'from the audio input.' },
              ],
              language: 'en',
            },
          },
        ],
      },
      latencyMs: Math.round(baseLatency + 500),
    };
  }

  if (modelName.includes('classifier') || modelName.includes('resnet')) {
    return {
      output: {
        model_name: modelName,
        model_version: '1',
        outputs: [
          {
            name: 'predictions',
            datatype: 'FP32',
            data: {
              class: 'golden_retriever',
              confidence: 0.9234,
              top_5: [
                { class: 'golden_retriever', confidence: 0.9234 },
                { class: 'labrador_retriever', confidence: 0.0421 },
                { class: 'cocker_spaniel', confidence: 0.0156 },
                { class: 'beagle', confidence: 0.0089 },
                { class: 'border_collie', confidence: 0.0045 },
              ],
            },
          },
        ],
      },
      latencyMs: Math.round(baseLatency + 8),
    };
  }

  if (modelName.includes('sentiment')) {
    return {
      output: {
        model_name: modelName,
        model_version: '1',
        outputs: [
          {
            name: 'sentiment',
            datatype: 'BYTES',
            data: {
              label: 'POSITIVE',
              score: 0.9456,
            },
          },
        ],
      },
      latencyMs: Math.round(baseLatency + 15),
    };
  }

  // Default response
  return {
    output: {
      model_name: modelName,
      model_version: '1',
      outputs: [
        {
          name: 'output',
          shape: [1, 1000],
          datatype: 'FP32',
          data: Array(5)
            .fill(0)
            .map(() => (Math.random()).toFixed(6))
            .concat(['...']),
        },
      ],
    },
    latencyMs: Math.round(baseLatency + 10),
  };
};
